import {
  ABSENCE_CATEGORIES,
  STATUSES,
  TIME_CATEGORIES,
  STANDARD_HOURS_CATEGORY,
  LOOKBACK_DAYS,
  TIMESHEET_START_DATE,
  normalizeTimesheetCategory,
} from './constants';
import { durationMinutes, timeToMinutes } from './otBands';
import { canCombineStandardHours, isWeekday } from './calc';
import { formatWorkDateDisplay } from './format';

function todayISO(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDaysISO(dateStr, delta) {
  const dt = new Date(`${dateStr}T12:00:00`);
  dt.setDate(dt.getDate() + delta);
  return todayISO(dt);
}

/** YYYY-MM-DD only; empty / invalid → null. */
export function normalizeTimesheetStartDate(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const dt = new Date(`${s}T12:00:00`);
  if (Number.isNaN(dt.getTime())) return null;
  return s;
}

/**
 * Sequential rule: prior dates that are draft (unsubmitted) or refused block
 * new submissions. Pending/Approved do not block.
 */
export function getSequentialBlockers(entries, { today = todayISO(), engineerId } = {}) {
  const mine = (entries || []).filter(e =>
    engineerId ? e.engineerId === engineerId : true,
  );

  const blockers = mine
    .filter(e => e.workDate && e.workDate < today)
    .filter(
      e => e.status === STATUSES.DRAFT || e.status === STATUSES.REFUSED,
    )
    .sort((a, b) => (a.workDate > b.workDate ? 1 : -1));

  return {
    blocked: blockers.length > 0,
    blockers,
    oldest: blockers[0] || null,
  };
}

/**
 * Earliest date the engineer should log next:
 * - oldest draft/refused date (any day, including weekend), else
 * - earliest missing weekday in lookback (not before timesheetStartDate), else
 * - today
 *
 * Idle Sat/Sun are not treated as missing (nothing valid to submit when WSH is
 * hidden). Draft/refused weekend entries still block via getSequentialBlockers.
 * Weekend exception logging is unchanged.
 *
 * Site-wide TIMESHEET_START_DATE floors the scan for all engineers.
 */
export function getEarliestAllowedWorkDate(
  entries,
  {
    today = todayISO(),
    engineerId,
    lookbackDays = LOOKBACK_DAYS,
    timesheetStartDate = TIMESHEET_START_DATE,
  } = {},
) {
  const seq = getSequentialBlockers(entries, { today, engineerId });
  if (seq.oldest) return seq.oldest.workDate;

  const mine = (entries || []).filter(e =>
    engineerId ? e.engineerId === engineerId : true,
  );
  const submittedDates = new Set(
    mine
      .filter(
        e =>
          e.status === STATUSES.PENDING || e.status === STATUSES.APPROVED,
      )
      .map(e => e.workDate),
  );

  const startFloor = normalizeTimesheetStartDate(timesheetStartDate);

  for (let i = lookbackDays; i >= 1; i -= 1) {
    const date = addDaysISO(today, -i);
    if (startFloor && date < startFloor) continue;
    // Idle weekends are not mandatory; draft/refused weekends already handled above
    if (!isWeekday(date)) continue;
    if (!submittedDates.has(date)) {
      const hasAny = mine.some(e => e.workDate === date);
      if (!hasAny) return date;
    }
  }

  return today;
}

/**
 * Two time ranges overlap (supports overnight).
 */
export function timesOverlap(aStart, aEnd, bStart, bEnd) {
  const a0 = timeToMinutes(aStart);
  const a1 = timeToMinutes(aEnd);
  const b0 = timeToMinutes(bStart);
  const b1 = timeToMinutes(bEnd);
  if ([a0, a1, b0, b1].some(v => v == null)) return false;

  const expand = (s, e) => {
    if (e > s) return [[s, e]];
    if (e === s) return [[s, s]];
    // overnight → two segments
    return [
      [s, 24 * 60],
      [0, e],
    ];
  };

  const segsA = expand(a0, a1);
  const segsB = expand(b0, b1);

  return segsA.some(([as, ae]) =>
    segsB.some(([bs, be]) => as < be && bs < ae),
  );
}

export function findOverlappingEntries(entry, existingEntries = []) {
  if (!TIME_CATEGORIES.includes(entry.category)) return [];
  if (!entry.startTime || !entry.endTime || !entry.workDate) return [];

  return existingEntries.filter(other => {
    if (entry.id && other.id === entry.id) return false;
    if (other.engineerId && entry.engineerId && other.engineerId !== entry.engineerId) {
      return false;
    }
    if (other.workDate !== entry.workDate) return false;
    if (!TIME_CATEGORIES.includes(other.category)) return false;
    if (!other.startTime || !other.endTime) return false;
    return timesOverlap(
      entry.startTime,
      entry.endTime,
      other.startTime,
      other.endTime,
    );
  });
}

const sameDayEntries = (entry, existingEntries = []) =>
  (existingEntries || []).filter(other => {
    if (entry.id && other.id === entry.id) return false;
    if (
      other.engineerId &&
      entry.engineerId &&
      other.engineerId !== entry.engineerId
    ) {
      return false;
    }
    return other.workDate === entry.workDate;
  });

const hasStandardHoursLogged = e =>
  !!e.workedStandardHours || e.category === STANDARD_HOURS_CATEGORY;

/** Block claiming Worked Standard Hours twice on the same calendar day. */
export function findSameDayStandardHoursConflict(entry, existingEntries = []) {
  if (!entry.workDate || !hasStandardHoursLogged(entry)) return null;
  const others = sameDayEntries(entry, existingEntries);
  if (others.some(hasStandardHoursLogged)) {
    return 'Worked Standard Hours can only be claimed once per day. Another entry on this date already has it.';
  }
  return null;
}

/** Block 8h standard + sickness/holiday/auth absence on the same calendar day. */
export function findSameDayAbsenceConflict(entry, existingEntries = []) {
  if (!entry.workDate) return null;
  const category = normalizeTimesheetCategory(entry);
  const others = sameDayEntries(entry, existingEntries);
  const loggingStandard = hasStandardHoursLogged(entry);
  const loggingAbsence = ABSENCE_CATEGORIES.includes(category);
  if (loggingStandard && others.some(o => ABSENCE_CATEGORIES.includes(o.category))) {
    return 'Standard hours cannot be logged on the same date as Sickness, Holiday, or Auth Absence.';
  }
  if (loggingAbsence && others.some(hasStandardHoursLogged)) {
    return 'Sickness, Holiday, or Auth Absence cannot be logged on a date that already has standard hours.';
  }
  return null;
}

/**
 * Validate entry payload. Returns { valid, errors: string[] }.
 */
export function validateEntry(
  entry,
  {
    existingEntries = [],
    today = todayISO(),
    enforceSequential = true,
    isSubmit = false,
    timesheetStartDate = TIMESHEET_START_DATE,
  } = {},
) {
  const errors = [];
  const category = normalizeTimesheetCategory(entry);

  if (!entry.workDate) errors.push('Work date is required.');
  if (!category) {
    errors.push(
      'Select an exception category, or tick Worked standard hours for a standard day.',
    );
  }
  if (category === STANDARD_HOURS_CATEGORY && !entry.workedStandardHours) {
    errors.push('Standard hours category requires Worked standard hours to be ticked.');
  }

  if (entry.workedStandardHours) {
    if (!isWeekday(entry.workDate)) {
      errors.push('Standard hours are only allowed Monday–Friday.');
    }
    if (category && !canCombineStandardHours(category)) {
      errors.push(
        'Standard hours cannot be combined with Sickness, Holiday, or Auth Absence.',
      );
    }
  }

  if (TIME_CATEGORIES.includes(category) && !entry.daySheetAbsence) {
    if (!entry.startTime || !entry.endTime) {
      errors.push('Start and end times are required for this category.');
    } else {
      const dur = durationMinutes(entry.startTime, entry.endTime);
      if (dur == null) errors.push('Invalid start/end time.');
      else if (dur <= 0) errors.push('End time must be after start time.');
      else if (dur > 16 * 60 && !entry.confirmLongShift) {
        errors.push(
          'This entry is longer than 16 hours. Confirm the times if that is right.',
        );
      }
    }
  }

  if (category === 'on_call_allowance') {
    if (
      entry.onCallAllowance === '' ||
      entry.onCallAllowance == null ||
      Number.isNaN(Number(entry.onCallAllowance)) ||
      Number(entry.onCallAllowance) < 0
    ) {
      errors.push('On-call allowance (£) is required and must be non-negative.');
    }
  }

  if (category === 'collecting_kit' && !entry.kitCollectionTime) {
    errors.push('Kit collection time is required when collecting kit.');
  }

  const overlaps = findOverlappingEntries(entry, existingEntries);
  if (overlaps.length > 0) {
    errors.push('Time range overlaps another entry on this date.');
  }

  const absenceConflict = findSameDayAbsenceConflict(entry, existingEntries);
  if (absenceConflict) errors.push(absenceConflict);

  const standardHoursConflict = findSameDayStandardHoursConflict(
    entry,
    existingEntries,
  );
  if (standardHoursConflict) errors.push(standardHoursConflict);

  if (enforceSequential && isSubmit) {
    const seq = getSequentialBlockers(existingEntries, {
      today,
      engineerId: entry.engineerId,
    });
    // Allow submitting/updating the oldest blocker itself
    const editingBlocker =
      entry.id && seq.blockers.some(b => b.id === entry.id);
    if (seq.blocked && !editingBlocker) {
      errors.push(
        `Sequential rule: resolve ${formatWorkDateDisplay(
          seq.oldest.workDate,
        ) || seq.oldest.workDate} (${seq.oldest.status}) before submitting a new entry.`,
      );
    }

    const earliest = getEarliestAllowedWorkDate(existingEntries, {
      today,
      engineerId: entry.engineerId,
      timesheetStartDate,
    });
    if (entry.workDate && entry.workDate > earliest && !editingBlocker) {
      errors.push(
        `Sequential rule: submit ${formatWorkDateDisplay(earliest) ||
          earliest} before logging ${formatWorkDateDisplay(entry.workDate) ||
          entry.workDate}.`,
      );
    }
  }

  return { valid: errors.length === 0, errors };
}

export function validateApproveRefuse({ status, fmComment }) {
  const errors = [];
  if (status !== STATUSES.APPROVED && status !== STATUSES.REFUSED) {
    errors.push('Status must be approved or refused.');
  }
  if (
    status === STATUSES.REFUSED &&
    (!fmComment || !String(fmComment).trim())
  ) {
    errors.push('A comment is required when refusing an entry.');
  }
  return { valid: errors.length === 0, errors };
}
